import React, { useState } from "react";
import PlacesInput from "../UI/GooglePlacesAutocomplete/PlacesInput";
import {
  ModalField,
  ModalFields,
  ModalForm,
  ModalInput,
  ModalNote,
} from "../../design-system/patterns/modal-form";

/**
 * The default address a seller application needs. One of the nine modal forms.
 *
 * Validation is unchanged, including the four-digit postal code rule that the
 * profile's own address form does not have. That difference is real and it is
 * not reconciled here: the registration endpoint is the one that rejects a
 * non-SA code, and changing either rule is a product decision, not a design one.
 *
 * `is_default` was a permanently disabled checkbox, which is a control that
 * cannot be operated pretending to be one. It is now a plain statement of fact,
 * because that is what it always was: this address IS the default, that is the
 * whole reason the dialog opened.
 */

export default function AddressModal({ isOpen, onClose, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    title: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "",
    postal_code: "",
    is_default: true,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePlaceSelect = (placeDetails) => {
    if (!placeDetails || !placeDetails.addressComponents) return;

    const components = placeDetails.addressComponents;
    let city = "";
    let province = "";
    let postalCode = "";

    components.forEach((component) => {
      const types = component.types;

      if (types.includes("postal_code")) {
        postalCode = component.long_name;
      }

      if (types.includes("locality") || types.includes("sublocality_level_1")) {
        city = component.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        province = component.long_name;
      }
    });

    setFormData((prev) => ({
      ...prev,
      address_line1: placeDetails.address || prev.address_line1,
      city: city || prev.city,
      province: province || prev.province,
      postal_code: postalCode || prev.postal_code,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Address title is required";
    }

    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = "Address line 1 is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.province.trim()) {
      newErrors.province = "Province is required";
    }

    if (!formData.postal_code.trim()) {
      newErrors.postal_code = "Postal code is required";
    } else if (!/^\d{4}$/.test(formData.postal_code)) {
      newErrors.postal_code = "Postal code must be 4 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData, { setSubmitting: () => {} });
    }
  };

  return (
    <ModalForm
      open={isOpen}
      title="Add default address"
      subtitle="Buyers never see this. It is where your payouts and any returns are registered."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={loading}
      submitLabel="Add address"
      submittingLabel="Adding"
    >
      <ModalFields cols={2}>
        <ModalField label="Address title" error={errors.title} full>
          <ModalInput
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Home, Work, Office"
            disabled={loading}
          />
        </ModalField>

        <ModalField label="Address line 1" error={errors.address_line1} full>
          <PlacesInput
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            onPlaceSelect={handlePlaceSelect}
            placeholder="Start typing your address"
            error={Boolean(errors.address_line1)}
          />
        </ModalField>

        <ModalField label="Address line 2" optional full>
          <ModalInput
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Apartment, suite, unit"
            disabled={loading}
          />
        </ModalField>

        <ModalField label="City" error={errors.city}>
          <ModalInput
            name="city"
            value={formData.city}
            onChange={handleChange}
            disabled={loading}
          />
        </ModalField>

        <ModalField label="Province" error={errors.province}>
          <ModalInput
            name="province"
            value={formData.province}
            onChange={handleChange}
            disabled={loading}
          />
        </ModalField>

        <ModalField
          label="Postal code"
          error={errors.postal_code}
          hint="Four digits"
        >
          <ModalInput
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            inputMode="numeric"
            maxLength={4}
            disabled={loading}
          />
        </ModalField>
      </ModalFields>

      <ModalNote>This becomes your default address.</ModalNote>
    </ModalForm>
  );
}
